import { useCallback, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { Button, Grid, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TaskEnums } from "../../../../common/constants";
import SimpleTable from "../../../common/tables/SimpleTable";
import UrgencyToolbar from "../common/UrgencyToolbar";
import { useDispatch } from "../providers/DispatchProvider";
import InfoToolbar from "../common/InfoToolbar";
import useDispatchColumns from "../common/useDispatchColumns";


const useStyles = makeStyles()(
  (theme) => ({
    surface: {
      borderRadius: theme.spacing(2),
      "& .MuiDataGrid-toolbarContainer": {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(2),
        paddingBottom: 0,
        paddingTop: theme.spacing(3)
      }
    },
    rejectedRow: {
      background: theme.palette.error.background,
      "&:hover": {
        background: `${theme.palette.error.backgroundFocus} !important`
      }
    },
  })
);


const DEFAULT_SETTINGS_TABLE = {
  visibleColumns: {
    created: false,
    type: false,
    cause: false,
    registration: false,
    description: false,
    details: false,
    eta: false,
  },
  sortModel: [
    {
      "field": "urgency",
      "sort": "desc"
    }
  ]
}

export default function DesktopDispatchView() {

  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tableFilterRef = useRef();

  const { showLoading, filteredActiveTasks, resetFilter, activeTasks } = useDispatch();
  const [renderedRowCount, setRenderedRowCount] = useState(-1);

  const onRowClick = useCallback((params, ev) => {
    navigate(`./${params.id}`, { relative: "path" });
  }, [navigate]);

  const columns = useDispatchColumns();

  const onGetRowClassName = useCallback((params) => {
    const urgency = params.row.urgency;
    return urgency === TaskEnums.EstimatedUrgency.Critical ? classes.rejectedRow : undefined;
  }, [classes]);

  const onStateChange = useCallback((state) => {
    if (activeTasks.length > 0) {
      const rows = state?.visibleRowsLookup;
      const value = filteredActiveTasks.length - Object.values(rows).filter(t => t === false).length;
      setRenderedRowCount(value)
    }
  }, [activeTasks.length, filteredActiveTasks.length]);

  const showFilterWarning = renderedRowCount !== activeTasks.length && renderedRowCount !== -1;

  const onResetFilters = useCallback(() => {
    tableFilterRef?.current?.onReset();
    resetFilter();
  }, [resetFilter]);

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="body" color="textSecondary">
          {showFilterWarning ? `Viser ${renderedRowCount} av ${activeTasks.length}` : `${filteredActiveTasks.length} totalt`}
          {showFilterWarning ? <Button color="info" onClick={onResetFilters}>(nullstill filter)</Button> : null}
        </Typography>
      </Grid>

      <Grid item xl="auto" lg={8} md={12} sm={12} xs={12}
        sx={{
          maxWidth: {
            sx: "400px",
            md: "500px",
            lg: "500px",
            xl: "850px"
          }
        }}
      >
        <UrgencyToolbar />
      </Grid>

      <Grid item>
        <InfoToolbar />
      </Grid>


      <Grid item xs={12}>
        <Paper className={classes.surface}>
          <SimpleTable
            persistentFilterRef={tableFilterRef}
            onStateChange={onStateChange}
            tableId="active_tasks_v2"
            loading={showLoading}
            rows={filteredActiveTasks}
            columns={columns}
            onRowClick={onRowClick}
            emptyStateTitle={t("dispatch:activeTasks:emptyStateTitle")}
            emptyStateDescription={t("dispatch:activeTasks:emptyStateDescription")}
            getRowClassName={onGetRowClassName}
            defaultSettings={DEFAULT_SETTINGS_TABLE}
            disablePaging
          />
        </Paper>
      </Grid>


    </>
  );
}